import React from 'react';
import { Controller, useFormContext, useFieldArray } from 'react-hook-form';
import {
  Typography,
  Grid,
  FormGroup,
  FormControlLabel,
  Checkbox,
  TextField,
  RadioGroup,
  Radio,
  Button,
  Divider,
  IconButton,
  Box,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

export default function AdverseEventItem({ index = 0, removeAE }) {
  const { control } = useFormContext();

  const baseName = `adverseEvents.${index}`;

  // 진단검사정보 동적배열
  const {
    fields: diagFields,
    append: diagAppend,
    remove: diagRemove,
  } = useFieldArray({
    control,
    name: `${baseName}.diagnosticTestInfo`,
  });

  // 라벨 매핑 (화면은 한글, 저장 key는 영문 유지)
  const labelMaps = {
    gastrointestinalSymptoms: {
      nausea: '구역',
      diarrhea: '설사',
      vomiting: '구토',
      abdominalPain: '복통',
      abdominalDistension: '복부 팽만',
      constipation: '변비',
      abdominalDiscomfort: '복부 불쾌감',
      indigestion: '소화 불량',
      gastrointestinalDisorder: '기타 위장관 장애',
    },
    skinSymptoms: { jaundice: '황달', rash: '발진', itching: '가려움', hives: '두드러기' },
    neuropsychiatricSymptoms: {
      headache: '두통',
      dizziness: '어지럼증',
      drowsiness: '졸림',
      anxiety: '불안',
      insomnia: '불면',
      tremor: '떨림',
    },
    systemicSymptoms: {
      lossOfAppetite: '식욕 부진',
      fatigueMalaiseLethargy: '피로/권태/무기력',
      weightLoss: '체중 감소',
      edema: '부종',
      sweating: '발한',
      abnormalLiverFunctionTest: '간기능 검사 이상',
      liverDysfunction: '간 기능 장애',
      abnormalBloodTest: '혈액 검사 이상',
      fever: '발열',
      hypertension: '고혈압',
      tachycardia: '빈맥',
      bradycardia: '서맥',
      xerostomia: '구강 건조',
    },
    entSymptoms: { xerostomia: '구강 건조' },
    genitourinarySymptoms: {
      darkColoredUrine: '혈뇨/진한 소변',
      dysuria: '배뇨 곤란',
      vaginalBleeding: '질 출혈',
    },
    respiratorySymptoms: { dyspnea: '호흡 곤란', cough: '기침' },
    cardiovascularSymptoms: {
      palpitations: '심계항진',
      chestTightness: '흉부 압박감',
      chestPain: '흉통',
      arrhythmia: '부정맥',
    },
    musculoskeletalSymptoms: { arthralgia: '관절통' },
    adverseEventSeverityCriteria: {
      resultsInDeath: '사망을 초래',
      lifeThreatening: '생명을 위협',
      requiresHospitalization: '입원 또는 입원기간 연장 필요',
      causesDisability: '지속적/중대한 장애나 기능 저하 초래',
      congenitalAnomaly: '선천성 기형 또는 이상 초래',
      medicallyImportantEvent: '기타 의학적으로 중요한 상황',
      notApplicable: '해당없음',
    },
  };

  // Boolean 그룹 렌더러
  const renderBoolGroup = (title, fieldName, keys) => (
    <Grid container spacing={1} sx={{ mt: 2 }}>
      <Grid size={{ xs: 12 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <FormGroup row>
          {keys.map((key) => {
            const id = `${baseName}-${fieldName}-${key}`;
            return (
              <Controller
                key={key}
                name={`${baseName}.${fieldName}.${key}`}
                control={control}
                render={({ field }) => (
                  <FormControlLabel
                    htmlFor={id}
                    control={
                      <Checkbox
                        {...field}
                        id={id}
                        name={`${baseName}.${fieldName}.${key}`}
                        checked={field.value}
                        onChange={field.onChange}
                      />
                    }
                    label={labelMaps[fieldName]?.[key] || key}
                  />
                )}
              />
            );
          })}
        </FormGroup>
      </Grid>
    </Grid>
  );

  return (
    <Box
      sx={{
        p: 2,
        mb: 2,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        bgcolor: 'background.paper',
      }}
    >
      <Typography variant="h6" gutterBottom>
        # {index + 1} 이상사례 / 약물이상반응 내용
      </Typography>

      <Grid container spacing={2} sx={{ display: 'flex', alignItems: 'flex-end', mt: 1 }}>
        {/* 1. 증상 내용 */}
        {renderBoolGroup('위장관', 'gastrointestinalSymptoms', [
          'nausea',
          'diarrhea',
          'vomiting',
          'abdominalPain',
          'abdominalDistension',
          'constipation',
          'abdominalDiscomfort',
          'indigestion',
          'gastrointestinalDisorder',
        ])}
        {renderBoolGroup('피부', 'skinSymptoms', ['jaundice', 'rash', 'itching', 'hives'])}
        {renderBoolGroup('신경정신계', 'neuropsychiatricSymptoms', [
          'headache',
          'dizziness',
          'drowsiness',
          'anxiety',
          'insomnia',
          'tremor',
        ])}
        {renderBoolGroup('전신증상', 'systemicSymptoms', [
          'lossOfAppetite',
          'fatigueMalaiseLethargy',
          'weightLoss',
          'edema',
          'sweating',
          'abnormalLiverFunctionTest',
          'liverDysfunction',
          'abnormalBloodTest',
          'fever',
          'hypertension',
          'tachycardia',
          'bradycardia',
          'xerostomia',
        ])}
        {renderBoolGroup('안이비인후', 'entSymptoms', ['xerostomia'])}
        {renderBoolGroup('비뇨생식계', 'genitourinarySymptoms', ['darkColoredUrine', 'dysuria', 'vaginalBleeding'])}
        {renderBoolGroup('호흡기', 'respiratorySymptoms', ['dyspnea', 'cough'])}
        {renderBoolGroup('심혈관', 'cardiovascularSymptoms', [
          'palpitations',
          'chestTightness',
          'chestPain',
          'arrhythmia',
        ])}
        {renderBoolGroup('근골격계', 'musculoskeletalSymptoms', ['arthralgia'])}

        {/* 기타 */}
        <Controller
          name={`${baseName}.other`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              label="기타"
              id={`${baseName}-other`}
              name={`${baseName}.other`}
              sx={{ ml: 2, width: 400 }}
            />
          )}
        />

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* 2,3. 날짜 */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={`${baseName}.adverseEventStartDate`}
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <TextField
                {...rest}
                value={value ?? ''}
                label="이상반응/이상사례 발생일"
                placeholder="YYYYMMDD"
                id={`${baseName}-startDate`}
                name={`${baseName}.adverseEventStartDate`}
                fullWidth
                // 숫자만, 최대 8자리
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, ''); // 숫자만 남김 [11]
                  const sliced = onlyDigits.slice(0, 8); // 최대 8자리 제한 [8]
                  onChange(sliced);
                }}
                // 모바일 숫자 키패드 유도 + 패턴 힌트
                inputProps={{
                  inputMode: 'numeric', // 모바일에서 숫자 키패드 유도 [7]
                  pattern: '\\d{0,8}', // 숫자 0~8자리 힌트 [7]
                }}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={`${baseName}.adverseEventEndDate`}
            control={control}
            render={({ field: { value, onChange, ...rest } }) => (
              <TextField
                {...rest}
                value={value ?? ''}
                label="이상반응/이상사례 종료일"
                placeholder="YYYYMMDD"
                id={`${baseName}-endDate`}
                name={`${baseName}.adverseEventEndDate`}
                fullWidth
                // 숫자만, 최대 8자리
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, ''); // 숫자만 남김 [11]
                  const sliced = onlyDigits.slice(0, 8); // 최대 8자리 제한 [8]
                  onChange(sliced);
                }}
                // 모바일 숫자 키패드 유도 + 패턴 힌트
                inputProps={{
                  inputMode: 'numeric', // 모바일에서 숫자 키패드 유도 [7]
                  pattern: '\\d{0,8}', // 숫자 0~8자리 힌트 [7]
                }}
              />
            )}
          />
        </Grid>

        {/* 4. 국가 */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Controller
            name={`${baseName}.adverseEventCountry`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="이상반응/이상사례가 발생한 국가"
                id={`${baseName}-country`}
                name={`${baseName}.adverseEventCountry`}
                fullWidth
              />
            )}
          />
        </Grid>

        {/* 5. 지속기간 */}
        <Grid size={{ xs: 12, md: 2 }}>
          <Controller
            name={`${baseName}.adverseEventDuration`}
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="지속 기간"
                type="number"
                id={`${baseName}-duration`}
                name={`${baseName}.adverseEventDuration`}
                fullWidth
                inputProps={{ min: 0 }}
                value={field.value ?? ''}
              />
            )}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 9 }}>
          <Typography variant="subtitle2">단위</Typography>
          <Controller
            name={`${baseName}.adverseEventDurationUnit`}
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field} name={`${baseName}.adverseEventDurationUnit`}>
                {['sec', 'min', 'hr', 'day', 'wk', 'mo', 'yr', 'etc'].map((v) => (
                  <FormControlLabel
                    key={v}
                    value={v}
                    control={<Radio id={`${baseName}-unit-${v}`} />}
                    label={{ sec: '초', min: '분', hr: '시', day: '일', wk: '주', mo: '월', yr: '년', etc: '기타' }[v]}
                  />
                ))}
              </RadioGroup>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* 6. 중대성 기준 */}
        {renderBoolGroup('이상반응 중대성 기준(Severity) (해당되는 경우 모두 표시)', 'adverseEventSeverityCriteria', [
          'resultsInDeath',
          'lifeThreatening',
          'requiresHospitalization',
          'causesDisability',
          'congenitalAnomaly',
          'medicallyImportantEvent',
          'notApplicable',
        ])}

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* 7. 병원 입원 여부 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
            이상반응으로 인한 병원 입원여부
          </Typography>
          <Controller
            name={`${baseName}.adverseEventHospitalization`}
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field} name={`${baseName}.adverseEventHospitalization`}>
                <FormControlLabel value="yes" control={<Radio id={`${baseName}-hosp-yes`} />} label="예" />
                <FormControlLabel value="no" control={<Radio id={`${baseName}-hosp-no`} />} label="아니오" />
              </RadioGroup>
            )}
          />
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Divider sx={{ my: 1 }} />
        </Grid>

        {/* 8. 최종 관찰 결과 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
            최종 관찰 시 이상사례/약물이상반응의 결과
          </Typography>
          <Controller
            name={`${baseName}.adverseEventOutcome`}
            control={control}
            render={({ field }) => (
              <RadioGroup row {...field} name={`${baseName}.adverseEventOutcome`}>
                <FormControlLabel value="recovered" control={<Radio id={`${baseName}-outcome-rec`} />} label="회복됨" />
                <FormControlLabel
                  value="recovering"
                  control={<Radio id={`${baseName}-outcome-ing`} />}
                  label="회복중"
                />
                <FormControlLabel
                  value="notRecovered"
                  control={<Radio id={`${baseName}-outcome-not`} />}
                  label="회복되지 않음"
                />
                <FormControlLabel
                  value="recoveredWithSequelae"
                  control={<Radio id={`${baseName}-outcome-seq`} />}
                  label="후유증 있음"
                />
                <FormControlLabel
                  value="fatalPossiblyRelated"
                  control={<Radio id={`${baseName}-outcome-fatal`} />}
                  label="치명적"
                />
                <FormControlLabel value="unknown" control={<Radio id={`${baseName}-outcome-unk`} />} label="알수없음" />
              </RadioGroup>
            )}
          />
        </Grid>

        {/* 9. 진단검사정보 */}
        <Grid size={{ xs: 12 }}>
          <Typography variant="subtitle1" sx={{ mt: 2, fontWeight: 600 }}>
            진단 검사 정보 (복수 작성 가능)
          </Typography>

          {diagFields.map((df, di) => {
            const dn = `${baseName}.diagnosticTestInfo.${di}`;
            const idBase = `${baseName}-diag-${di}`;
            return (
              <Grid
                container
                spacing={1}
                key={df.id}
                sx={{ mt: 2, mb: 1, p: 1, border: '1px solid #ddd', borderRadius: 1, position: 'relative' }}
              >
                {/* 검사 삭제 버튼 - 우측 상단 */}
                <IconButton
                  color="error"
                  onClick={() => diagRemove(di)}
                  sx={{ position: 'absolute', top: 4, right: 4 }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>

                {/* 검사일 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${dn}.testDate`}
                    control={control}
                    render={({ field: { value, onChange, ...rest } }) => (
                      <TextField
                        {...rest}
                        value={value ?? ''}
                        label="검사일"
                        placeholder="YYYYMMDD"
                        id={`${idBase}-date`}
                        name={`${dn}.testDate`}
                        fullWidth
                        // 숫자만, 최대 8자리
                        onChange={(e) => {
                          const onlyDigits = e.target.value.replace(/\D/g, ''); // 숫자만 남김 [11]
                          const sliced = onlyDigits.slice(0, 8); // 최대 8자리 제한 [8]
                          onChange(sliced);
                        }}
                        // 모바일 숫자 키패드 유도 + 패턴 힌트
                        inputProps={{
                          inputMode: 'numeric', // 모바일에서 숫자 키패드 유도 [7]
                          pattern: '\\d{0,8}', // 숫자 0~8자리 힌트 [7]
                        }}
                      />
                    )}
                  />
                </Grid>

                {/* 검사명 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${dn}.testName`}
                    control={control}
                    render={({ field }) => (
                      <TextField {...field} label="검사명" id={`${idBase}-name`} name={`${dn}.testName`} fullWidth />
                    )}
                  />
                </Grid>

                {/* 검사결과 라디오 */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Typography variant="subtitle2">검사 결과</Typography>
                  <Controller
                    name={`${dn}.testResult`}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup row {...field} name={`${dn}.testResult`}>
                        <FormControlLabel value="positive" control={<Radio id={`${idBase}-res-pos`} />} label="양성" />
                        <FormControlLabel value="negative" control={<Radio id={`${idBase}-res-neg`} />} label="음성" />
                        <FormControlLabel
                          value="borderline"
                          control={<Radio id={`${idBase}-res-bor`} />}
                          label="경계"
                        />
                        <FormControlLabel
                          value="inconclusive"
                          control={<Radio id={`${idBase}-res-inc`} />}
                          label="판정불가"
                        />
                      </RadioGroup>
                    )}
                  />
                </Grid>

                {/* 값/한정기호 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${dn}.testResultOp`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="값/한정기호"
                        id={`${idBase}-op`}
                        name={`${dn}.testResultOp`}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* 단위 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${dn}.testResultUnit`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="단위"
                        id={`${idBase}-unit`}
                        name={`${dn}.testResultUnit`}
                        fullWidth
                      />
                    )}
                  />
                </Grid>

                {/* 하한값 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${dn}.referenceRangeLower`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="정상범위 하한"
                        type="number"
                        id={`${idBase}-low`}
                        name={`${dn}.referenceRangeLower`}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                    )}
                  />
                </Grid>

                {/* 상한값 */}
                <Grid size={{ xs: 12, md: 3 }}>
                  <Controller
                    name={`${dn}.referenceRangeUpper`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="정상범위 상한"
                        type="number"
                        id={`${idBase}-up`}
                        name={`${dn}.referenceRangeUpper`}
                        fullWidth
                        inputProps={{ min: 0 }}
                      />
                    )}
                  />
                </Grid>

                {/* 상세내용 */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name={`${dn}.resultDescription`}
                    control={control}
                    defaultValue=""
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="검사 결과에 대한 상세내용"
                        id={`${idBase}-desc`}
                        name={`${dn}.resultDescription`}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    )}
                  />
                </Grid>

                {/* 비고 */}
                <Grid size={{ xs: 12, md: 6 }}>
                  <Controller
                    name={`${dn}.remarks`}
                    control={control}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        label="비고"
                        id={`${idBase}-remarks`}
                        name={`${dn}.remarks`}
                        fullWidth
                        multiline
                        minRows={2}
                      />
                    )}
                  />
                </Grid>

                {/* 추가정보 보유 여부 라디오 */}
                <Grid size={{ xs: 12 }}>
                  <Typography variant="subtitle2">추가정보 보유 여부</Typography>
                  <Controller
                    name={`${dn}.hasAdditionalInfo`}
                    control={control}
                    render={({ field }) => (
                      <RadioGroup
                        row
                        name={`${dn}.hasAdditionalInfo`}
                        {...field}
                        // value={field.value || ''}
                        // onChange={(e) => field.onChange(e.target.value === 'yes')}
                      >
                        <FormControlLabel value="yes" control={<Radio id={`${idBase}-add-yes`} />} label="예" />
                        <FormControlLabel value="no" control={<Radio id={`${idBase}-add-no`} />} label="아니오" />
                      </RadioGroup>
                    )}
                  />
                </Grid>

                {/* 삭제 버튼
                <Grid size={{ xs: 12 }}>
                  <Button size="small" color="error" onClick={() => diagRemove(di)}>
                    검사 삭제
                  </Button>
                </Grid> */}
              </Grid>
            );
          })}

          {/* 추가 버튼 */}
          <Button
            variant="outlined"
            size="small"
            onClick={() =>
              diagAppend({
                testDate: '',
                testName: '',
                testResult: '',
                testResultOp: '',
                testResultUnit: '',
                referenceRangeLower: '',
                referenceRangeUpper: '',
                resultDescription: '',
                remarks: '',
                hasAdditionalInfo: false,
              })
            }
          >
            검사 추가
          </Button>
        </Grid>

        <Grid size={{ xs: 12 }}>
          <Box sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
            {removeAE && (
              <Button variant="outlined" color="error" startIcon={<DeleteOutlineIcon />} onClick={removeAE}>
                이상사례 삭제
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}
